/*
 * Animated Brand Name Marquee - seamless infinite scroll (right to left)
 * Uses pure CSS animation (translateX keyframes) for guaranteed GPU-accelerated
 * animation on all browsers including iOS Safari. No JavaScript RAF needed.
 *
 * All styles are inline to avoid any Tailwind class override (e.g. .flex { min-width:0 })
 * collapsing the track width and breaking the animation.
 *
 * When real logos are available:
 *   1. Upload via: manus-upload-file --webdev path/to/logo.png
 *   2. Add a logoUrl to the client entry and the component will render the image instead.
 */

interface ClientLogo {
  name: string;
  /** Once you have the real logo, set this to the uploaded URL */
  logoUrl?: string;
}

const clients: ClientLogo[] = [
  { name: "Vodafone" },
  { name: "Philips" },
  { name: "PMI" },
  { name: "Unilever" },
  { name: "Nissan" },
  { name: "Wella DACH" },
  { name: "Beautifect" },
  { name: "Lucy Choi London" },
  { name: "Beam Suntory" },
  { name: "Digitas" },
  { name: "MoneyDashboard" },
  { name: "Elements.io" },
  { name: "Brew Tea" },
  { name: "Kenya Travel Board" },
  { name: "ICP" },
];

export default function Clients() {
  // The second visual pass makes the CSS loop seamless. It is deliberately
  // hidden from assistive technologies so each client name is announced once.
  const renderClientItems = (copy: "primary" | "loop") => clients.map((client, index) => (
    <span
      key={`${copy}-${client.name}-${index}`}
      style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }}
    >
      <span style={{ padding: "0.25rem 2rem", display: "inline-flex", alignItems: "center" }}>
        {client.logoUrl ? (
          <img
            src={client.logoUrl}
            alt={`${client.name} logo`}
            style={{ height: "28px", width: "auto", objectFit: "contain", opacity: 0.55 }}
            height={28}
            width={120}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <span
            style={{
              color: "rgba(255,255,255,0.6)",
              fontSize: "1.25rem",
              fontFamily: "system-ui, sans-serif",
              letterSpacing: "0.025em",
              userSelect: "none",
              whiteSpace: "nowrap",
            }}
            aria-label={client.name}
          >
            {client.name}
          </span>
        )}
      </span>
      <span
        style={{ color: "rgba(255,140,0,0.4)", fontSize: "1.125rem", userSelect: "none", flexShrink: 0 }}
        aria-hidden="true"
        role="presentation"
      >
        &middot;
      </span>
    </span>
  ));

  return (
    <section
      id="clients"
      style={{
        paddingTop: "2rem",
        paddingBottom: "2.5rem",
        background: "#0F1419",
        borderTop: "1px solid rgba(255,255,255,0.05)",
        overflow: "hidden",
      }}
      aria-label="Trusted by leading brands"
    >
      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 1rem", marginBottom: "1.5rem" }}>
        <p style={{
          color: "#FF8C00",
          fontSize: "0.75rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          textAlign: "center",
          fontFamily: "system-ui, sans-serif",
          fontWeight: 600,
          margin: 0,
        }}>
          Trusted by ambitious brands for AI marketing
        </p>
      </div>

      <div style={{
        position: "relative",
        width: "100%",
        overflow: "hidden",
        WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)",
        maskImage: "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)",
      }}>
        <div
          className="clients-marquee-track"
          style={{
            display: "inline-flex",
            flexDirection: "row",
            alignItems: "center",
            flexWrap: "nowrap",
            width: "max-content",
            minWidth: "max-content",
            flexShrink: 0,
            animationPlayState: "running",
            willChange: "transform",
            transform: "translate3d(0, 0, 0)",
            WebkitTransform: "translate3d(0, 0, 0)",
            backfaceVisibility: "hidden",
          }}
        >
          {renderClientItems("primary")}
          <span aria-hidden="true">{renderClientItems("loop")}</span>
        </div>
      </div>
    </section>
  );
}
